import React, { Fragment, useEffect, useState } from "react";
import {
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ButtonLoader from "../../../../../../share-components/ui/buttonLoader";
import {
  getServiceRateData,
  updateServiceRates,
  downloadServiceInvoicePdf,
} from "../../../../store/saleOrderInvoiceSlice";

const isValidRate = (value) => {
  if (value === "" || value === null || value === undefined) {
    return false;
  }
  const number = Number(value);
  return !Number.isNaN(number) && number >= 0;
};

const ServiceInvoiceModal = (props) => {
  const { isOpen, setOpen, id, invoiceNo } = props;
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [btnDisable, setBtnDisable] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && id) {
      setLoading(true);
      dispatch(getServiceRateData(id)).then((res) => {
        setLoading(false);
        if (!res.error) {
          setCurrencySymbol(res.payload.currency_symbol || "");
          setRows(
            (res.payload.line_items || []).map((item) => ({
              ...item,
              service_rate:
                item.service_rate !== null && item.service_rate !== undefined
                  ? item.service_rate
                  : "",
            }))
          );
        }
      });
    }
  }, [isOpen, id]);

  const closeDialog = () => {
    setOpen(false);
  };

  const onRateChange = (rowId, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, service_rate: value } : r))
    );
  };

  const hasInvalidRate = rows.some((r) => !isValidRate(r.service_rate));

  const buildLineItems = () =>
    rows.map((r) => ({
      id: r.id,
      service_price: Number(r.service_rate),
    }));

  const save = () => {
    setBtnDisable(true);
    return dispatch(
      updateServiceRates({ id, line_items: buildLineItems() })
    ).then((res) => {
      setBtnDisable(false);
      return res;
    });
  };

  const downloadPdf = () => {
    return dispatch(downloadServiceInvoicePdf({ id })).then((res) => {
      if (res.error || !res.payload) {
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.payload]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${invoiceNo}_Service-Invoice_${new Date().toJSON().slice(0, 10)}.pdf`
      );
      document.body.appendChild(link);
      link.click();
    });
  };

  const onUpdate = () => {
    if (hasInvalidRate) {
      toast.error("Enter a valid service rate for every line item");
      return;
    }
    save();
  };

  const onUpdateAndDownload = () => {
    if (hasInvalidRate) {
      toast.error("Enter a valid service rate for every line item");
      return;
    }
    setBtnDisable(true);
    save().then((res) => {
      if (res.error) {
        setBtnDisable(false);
        return;
      }
      downloadPdf().finally(() => setBtnDisable(false));
    });
  };

  return (
    <Fragment>
      <Modal size="lg" isOpen={isOpen} toggle={closeDialog}>
        <ModalHeader toggle={closeDialog}>
          Service Invoice {invoiceNo ? `- ${invoiceNo}` : ""}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <Table bordered responsive size="sm">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th className="text-center">Qty</th>
                  <th className="text-right">Local Rate</th>
                  <th className="text-right">Service Rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const invalid = !isValidRate(row.service_rate);
                  return (
                    <tr key={row.id}>
                      <td>{row.sku}</td>
                      <td>{row.name}</td>
                      <td className="text-center">{row.quantity}</td>
                      <td className="text-right">
                        {currencySymbol}
                        {Number(row.local_rate).toFixed(2)}
                      </td>
                      <td className="text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          invalid={invalid}
                          value={row.service_rate}
                          onChange={(e) =>
                            onRateChange(row.id, e.target.value)
                          }
                          style={{ maxWidth: 120, marginLeft: "auto" }}
                        />
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No line items
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <div
          className="mb-4"
          style={{
            borderTop: "1px solid #dee2e6",
            padding: "12px 16px",
          }}
        >
          <Row>
            <Col md={12} className="text-right">
              <ButtonLoader
                type="button"
                color="secondary"
                className="btn btn-secondary ml-1"
                btntext="Update"
                disabled={btnDisable || loading}
                onClick={onUpdate}
              />
              <ButtonLoader
                type="button"
                color="success"
                className="btn btn-success ml-1"
                btntext="Update & Download Service Invoice"
                disabled={btnDisable || loading}
                onClick={onUpdateAndDownload}
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </Fragment>
  );
};

export default ServiceInvoiceModal;
